import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  Mail,
  CreditCard,
  Wifi,
  Calendar,
  AlertTriangle,
  Loader2,
  MessageSquare,
  Ticket,
  History,
  Shield,
  ShieldCheck,
  MoreVertical,
  Ban,
  Lock,
  StickyNote,
  GitMerge,
  Download,
  Trash2,
  X,
  Globe,
  ChevronDown,
} from 'lucide-react';
import { api, inboxApi, type ContactNote, type Subscriber360 } from '../lib/api';
import { LineActionModal, type LineActionType } from './LineActionModal';
import { CreateTicketModal } from './CreateTicketModal';
import { ManualVerifyModal } from './ManualVerifyModal';
import { WpCustomerPanel } from './WpCustomerPanel';
import { WpSubscriptionsPanel } from './WpSubscriptionsPanel';
import { WpLinesPanel } from './WpLinesPanel';
import { WpDebtLedgerModal } from './WpDebtLedgerModal';
import { WpVerifyControls } from './WpVerifyControls';
import { useAuth } from '../hooks/useAuth';
import { useUiMode } from '../lib/uiMode';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface AiContextView {
  location?: 'israel' | 'abroad' | null;
  device?: 'iphone' | 'android' | null;
  topic?: string;
  isLead?: boolean;
}

interface Props {
  tenantCode: string;
  contactId: string;
  currentChatId?: string;
  agentUid?: string;
  /** Deep-link hint: open a specific modal/tab on mount (from the inbox context menu). */
  initialAction?: 'panel' | 'verify' | 'ticket' | 'lines' | 'notes' | 'block';
  /**
   * Simple view only: skip the collapsed summary and open the full panel. For
   * call sites that ARE the reveal (the mobile info drawer) — the agent already
   * tapped once to get here, a chip they have to tap again is a step backwards.
   */
  startExpanded?: boolean;
}

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-red-100 text-red-800',
  pending: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-gray-100 text-gray-700',
};

function formatDate(ms?: number | string): string {
  if (!ms) return '–';
  const d = typeof ms === 'string' ? new Date(ms) : new Date(ms);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toLocaleDateString();
}

export function SubscriberPanel({
  tenantCode,
  contactId,
  currentChatId,
  agentUid,
  initialAction,
  startExpanded,
}: Props) {
  // Simple view drops the technical/rare rows (AI quota, client IP + geo
  // lookup, block, merge). Customer data, verification and the tabs are the
  // same in both modes.
  //
  // It also OPENS as a summary: one chip saying who this is and whether they
  // are verified, plus the one action that state calls for. Most replies need
  // nothing else, and everything else is one click behind the chip. Advanced
  // renders the whole panel on mount exactly as it always did.
  const { advanced } = useUiMode();
  const { claims } = useAuth();
  const [data, setData] = useState<Subscriber360 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  /** Simple view only: the summary chip has been opened into the full panel. */
  const [detailsOpen, setDetailsOpen] = useState(Boolean(startExpanded));
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [actionModal, setActionModal] = useState<{ action: LineActionType; lineId: string; phoneNumber?: string } | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [notes, setNotes] = useState<ContactNote[]>([]);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'subs' | 'history' | 'notes'>('overview');
  const [noteDraft, setNoteDraft] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [aiQuota, setAiQuota] = useState<{ count: number; limit: number; keyKind: string | null; identified: boolean } | null>(null);
  const [clientIp, setClientIp] = useState<string | null>(null);
  const [wpCustomer, setWpCustomer] = useState<{
    display_name?: string;
    first_name?: string;
    last_name?: string;
    billing_phone?: string;
  } | null>(null);
  const [ipGeo, setIpGeo] = useState<
    | { city?: string; regionName?: string; country?: string; countryCode?: string; isp?: string; lat?: number; lon?: number; loading?: boolean; error?: string }
    | null
  >(null);
  // AI-derived conversation context (location / device / topic / sales-lead),
  // written to chats/<id>.aiContext by the bot each turn (feedback pass 2, item 6).
  const [aiContext, setAiContext] = useState<AiContextView | null>(null);
  // Live verification level off the chat doc. The Subscriber360 `data.verified`
  // is a REST snapshot taken on mount and goes stale when the customer self-
  // verifies mid-chat (last-4 flow) — that's why the panel showed "unverified"
  // while the live chat was already verified. Drive the badge off the live doc too.
  const [liveAuthLevel, setLiveAuthLevel] = useState<string | null>(null);
  useEffect(() => {
    if (!currentChatId) {
      setAiContext(null);
      setLiveAuthLevel(null);
      return;
    }
    const unsub = onSnapshot(
      doc(db, 'chats', currentChatId),
      (snap) => {
        const d = snap.data() as { aiContext?: AiContextView; authLevel?: string } | undefined;
        setAiContext(d?.aiContext ?? null);
        setLiveAuthLevel(d?.authLevel ?? null);
      },
      () => { setAiContext(null); setLiveAuthLevel(null); },
    );
    return () => unsub();
  }, [currentChatId]);

  // Honor a deep-link action from the inbox context menu (?cust=...).
  useEffect(() => {
    if (!initialAction) return;
    // The inbox menu sent the agent HERE. Don't land them on a chip they have
    // to open again to reach the tab/modal they asked for.
    setDetailsOpen(true);
    if (initialAction === 'verify') setShowVerifyModal(true);
    else if (initialAction === 'ticket') setShowTicketModal(true);
    else if (initialAction === 'block') setShowBlockModal(true);
    else if (initialAction === 'notes') setActiveTab('notes');
    else if (initialAction === 'lines') setActiveTab('overview');
  }, [initialAction]);

  // TODO: contact.blocked is not part of Subscriber360 today. Agent 1 stores it
  // on the contact doc. Until /agent/subscriber surfaces it (or a dedicated
  // contact-status endpoint exists), treat the panel as never-blocked and
  // expose Block + force-Unblock as separate buttons.
  const isBlocked = Boolean((data as unknown as { blocked?: boolean } | null)?.blocked);

  async function refresh() {
    try {
      const d = await api.getSubscriber(tenantCode, contactId, currentChatId);
      setData(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function reloadNotes() {
    try {
      const r = await inboxApi.getContactNotes(tenantCode, contactId);
      setNotes(r.items ?? []);
    } catch {
      // Endpoint may not exist yet
      setNotes([]);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getSubscriber(tenantCode, contactId, currentChatId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantCode, contactId, currentChatId]);

  // Load notes
  useEffect(() => {
    let cancelled = false;
    inboxApi
      .getContactNotes(tenantCode, contactId)
      .then((r) => {
        if (!cancelled) setNotes(r.items ?? []);
      })
      .catch(() => {
        if (!cancelled) setNotes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantCode, contactId]);

  // Best-effort view audit
  useEffect(() => {
    if (!agentUid) return;
    inboxApi.auditSubscriberView(tenantCode, agentUid, contactId).catch(() => {
      /* route may not exist yet */
    });
  }, [tenantCode, contactId, agentUid]);

  // WP customer name/phone (WooCommerce) for the header — verified WP tenants only.
  useEffect(() => {
    if (!currentChatId || !data?.verified || !data?.wpConfigured) {
      setWpCustomer(null);
      return;
    }
    let cancelled = false;
    inboxApi
      .wpCustomer(tenantCode, currentChatId)
      .then((c) => {
        if (!cancelled && c?.found) setWpCustomer(c);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tenantCode, currentChatId, data?.verified, data?.wpConfigured]);

  // AI quota — today's used/limit for the customer behind this chat.
  // Refreshes when the chat changes; passive (no auto-poll) — agents who
  // want the latest can re-select the chat.
  useEffect(() => {
    if (!currentChatId) {
      setAiQuota(null);
      return;
    }
    let cancelled = false;
    inboxApi
      .getAiQuota(tenantCode, currentChatId)
      .then((q) => {
        if (cancelled) return;
        setAiQuota({ count: q.count, limit: q.limit, keyKind: q.keyKind, identified: q.identified });
        setClientIp(q.deviceInfo?.ip ?? null);
        setIpGeo(null);
      })
      .catch(() => {
        if (cancelled) return;
        setAiQuota(null);
        setClientIp(null);
        setIpGeo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantCode, currentChatId]);

  async function addNote() {
    if (!agentUid || !noteDraft.trim()) return;
    setNoteBusy(true);
    try {
      await inboxApi.addContactNote(tenantCode, agentUid, contactId, noteDraft.trim());
      setNoteDraft('');
      await reloadNotes();
    } catch (e) {
      setActionMsg(`Failed to add note: ${e instanceof Error ? e.message : e}`);
    } finally {
      setNoteBusy(false);
    }
  }

  async function doBlock(reason: string) {
    if (!agentUid) return;
    setActionBusy(true);
    try {
      await inboxApi.blockContact(tenantCode, agentUid, contactId, reason);
      setShowBlockModal(false);
      setActionMsg('Contact blocked.');
      await refresh();
    } catch (e) {
      setActionMsg(`Block failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setActionBusy(false);
    }
  }

  async function doUnblock() {
    if (!agentUid) return;
    if (!confirm('Unblock this contact?')) return;
    setActionBusy(true);
    try {
      await inboxApi.unblockContact(tenantCode, agentUid, contactId);
      setActionMsg('Contact unblocked.');
      await refresh();
    } catch (e) {
      setActionMsg(`Unblock failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setActionBusy(false);
    }
  }

  async function doExport() {
    if (!agentUid) return;
    // GDPR full-PII export — confirm before downloading the entire contact record,
    // all chats, all messages and audit logs (was a one-click unconfirmed download).
    if (!window.confirm(
      'Download a FULL personal-data export for this contact?\n\nThis includes their profile, every chat and message, and audit logs. Use only for a verified GDPR/data-access request.'
    )) return;
    setExporting(true);
    try {
      await inboxApi.exportContactData(tenantCode, agentUid, contactId);
      setActionMsg('Export downloaded.');
    } catch (e) {
      setActionMsg(`Export failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setExporting(false);
    }
  }

  async function doDelete(reason: string) {
    if (!agentUid) return;
    setActionBusy(true);
    try {
      await inboxApi.deleteContactData(tenantCode, agentUid, contactId, reason);
      setShowDeleteModal(false);
      setActionMsg('Contact data deletion requested.');
    } catch (e) {
      setActionMsg(`Delete failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setActionBusy(false);
    }
  }

  async function doMerge(primaryChatId: string, secondaryChatId: string) {
    if (!agentUid) return;
    setActionBusy(true);
    try {
      await inboxApi.mergeChats(tenantCode, agentUid, primaryChatId, secondaryChatId);
      setShowMergeModal(false);
      setActionMsg('Conversations merged.');
      await refresh();
    } catch (e) {
      setActionMsg(`Merge failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setActionBusy(false);
    }
  }

  if (loading) {
    return (
      <aside className="w-72 border-l border-gray-200 bg-white p-3 text-sm text-gray-500 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading customer…
      </aside>
    );
  }
  if (error) {
    return (
      <aside className="w-72 border-l border-gray-200 bg-white p-3 text-sm">
        <div className="flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      </aside>
    );
  }
  if (!data) return null;

  const { identity, subscriber, availablePlans, verified } = data;
  // Prefer the WooCommerce name/phone when available (WP-backed tenants).
  const wpName = wpCustomer
    ? wpCustomer.display_name || `${wpCustomer.first_name ?? ''} ${wpCustomer.last_name ?? ''}`.trim()
    : '';
  const name =
    wpName ||
    identity.name ||
    (subscriber?.customer
      ? `${subscriber.customer.firstName ?? ''} ${subscriber.customer.lastName ?? ''}`.trim()
      : null) ||
    identity.phone ||
    identity.email ||
    identity.contactId;
  const headerPhone = wpCustomer?.billing_phone || subscriber?.msisdn || identity.phone;
  // Verified if the REST snapshot says so OR the live chat doc already shows a
  // verified auth level (self-verify can flip mid-chat after the REST fetch).
  const isVerified = verified === true || liveAuthLevel === 'verified';
  // Collapsed to the chip. Never true in advanced view.
  const summaryOnly = !advanced && !detailsOpen;

  return (
    <aside
      className={`${summaryOnly ? 'w-64' : 'w-80'} h-full border-l border-gray-200 bg-white overflow-y-auto flex-shrink-0`}
    >
      {isBlocked && (
        <div className="px-3 py-2 bg-red-50 border-b border-red-200 text-red-800 text-xs flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1 font-semibold">
            <Ban className="w-3.5 h-3.5" /> BLOCKED
          </span>
          {agentUid && (
            <button
              type="button"
              onClick={doUnblock}
              disabled={actionBusy}
              className="px-2 py-0.5 text-[11px] bg-white border border-red-300 rounded text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Unblock
            </button>
          )}
        </div>
      )}

      {/*
        The simple view's ENTIRE right panel: who this is, whether they are
        verified, and the one thing that state asks an agent to do. Tapping the
        chip opens the real panel — subscriptions, lines, history, notes and
        the verification detail are all still there, one click away.
      */}
      {summaryOnly && (
        <div className="p-2 space-y-1.5">
          <button
            type="button"
            onClick={() => setDetailsOpen(true)}
            aria-expanded={false}
            title="Show subscriptions, lines, history and notes"
            className="w-full flex items-center gap-2 pl-2 pr-1.5 py-1.5 rounded-full border border-gray-300 bg-white text-left hover:bg-gray-50"
          >
            {isVerified ? (
              <ShieldCheck className="w-4 h-4 flex-shrink-0 text-green-600" />
            ) : (
              <Shield className="w-4 h-4 flex-shrink-0 text-amber-600" />
            )}
            <span className="min-w-0 flex-1">
              <span className="block text-xs font-semibold text-gray-900 truncate">{name}</span>
              <span className={`block text-[10px] ${isVerified ? 'text-green-700' : 'text-amber-700'}`}>
                {isVerified ? 'verified' : 'unverified'}
                {subscriber?.status ? ` · ${subscriber.status}` : ''}
              </span>
            </span>
            <ChevronDown className="w-4 h-4 flex-shrink-0 text-gray-400" />
          </button>

          {/*
            The ONE action. Unverified is the state that actually needs one, and
            it has to stay "Verify (last-4)" — that button was reported as doing
            nothing and was fixed; collapsing it out of reach would re-break the
            same complaint. Gated on currentChatId only, exactly like the modal
            it opens: a guard the modal doesn't share is one more way for a
            button to do nothing.
          */}
          {!isVerified && currentChatId ? (
            <button
              type="button"
              onClick={() => setShowVerifyModal(true)}
              title="Opens the last-4 form so you can enter the digits the customer reads out. The card is checked against the real account."
              className="w-full inline-flex items-center justify-center gap-1 px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Verify (last-4)
            </button>
          ) : agentUid ? (
            <button
              type="button"
              onClick={() => setShowTicketModal(true)}
              className="w-full inline-flex items-center justify-center gap-1 px-2 py-1 text-xs bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            >
              <Ticket className="w-3.5 h-3.5" /> Create ticket
            </button>
          ) : null}
        </div>
      )}

      {!summaryOnly && (
        <div className="p-3 border-b border-gray-100">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">{name}</div>
              {subscriber?.status && (
                <span
                  className={`inline-block mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                    STATUS_BADGE[subscriber.status.toLowerCase()] ?? 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {subscriber.status}
                </span>
              )}
              {!isVerified && (
                <span className="inline-flex items-center gap-1 mt-1 ml-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                  <Shield className="w-3 h-3" /> unverified
                </span>
              )}
              {isVerified && (
                <span className="inline-flex items-center gap-1 mt-1 ml-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-800">
                  <ShieldCheck className="w-3 h-3" /> verified
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {advanced && agentUid && isVerified && !isBlocked && (
                <button
                  type="button"
                  onClick={() => setShowBlockModal(true)}
                  disabled={actionBusy}
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-white border border-red-300 rounded text-red-700 hover:bg-red-50 disabled:opacity-50"
                  title="Block this contact"
                >
                  <Ban className="w-3 h-3" /> Block
                </button>
              )}
              <button
                onClick={() => (advanced ? setCollapsed((v) => !v) : setDetailsOpen(false))}
                className="text-xs text-gray-500 hover:text-gray-700"
                title={advanced ? (collapsed ? 'Expand' : 'Collapse') : 'Back to summary'}
              >
                {advanced && collapsed ? '+' : '−'}
              </button>
            </div>
          </div>
          <div className="mt-2 space-y-1 text-xs text-gray-600">
            {identity.email && (
              <div className="flex items-center gap-1.5">
                <Mail className="w-3 h-3" />
                <span className="truncate">{identity.email}</span>
              </div>
            )}
            {headerPhone && (
              <div className="flex items-center gap-1.5">
                <Phone className="w-3 h-3" />
                <span dir="ltr">{headerPhone}</span>
              </div>
            )}
            {subscriber?.activatedAt && (
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3 h-3" />
                <span>since {formatDate(subscriber.activatedAt)}</span>
              </div>
            )}
            {advanced && aiQuota && (
              <div
                className="flex items-center gap-1.5"
                title={`AI message quota tracks this customer's daily usage. Resets at UTC midnight. Limit is ${
                  aiQuota.identified ? '500/day for identified users' : '100/day for anonymous traffic (per IP)'
                }. When exceeded, the chat returns 429 and no AI reply is generated.`}
              >
                <Shield className="w-3 h-3" />
                <span>
                  AI today:{' '}
                  <span
                    className={
                      aiQuota.count >= aiQuota.limit
                        ? 'font-semibold text-red-600'
                        : aiQuota.count >= aiQuota.limit * 0.8
                          ? 'font-semibold text-amber-600'
                          : 'font-medium text-gray-700'
                    }
                  >
                    {aiQuota.count} / {aiQuota.limit}
                  </span>
                  <span className="text-gray-400"> · {aiQuota.keyKind ?? 'n/a'}</span>
                </span>
              </div>
            )}
            {advanced && clientIp && (
              <div className="flex items-start gap-1.5">
                <Globe className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span dir="ltr" className="font-mono text-[11px] truncate">{clientIp}</span>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!clientIp) return;
                        setIpGeo({ loading: true });
                        try {
                          const r = await inboxApi.ipLookup(clientIp);
                          if (r.status === 'fail') {
                            setIpGeo({ error: r.message || 'lookup failed' });
                          } else {
                            setIpGeo({
                              city: r.city,
                              regionName: r.regionName,
                              country: r.country,
                              countryCode: r.countryCode,
                              isp: r.isp,
                              lat: r.lat,
                              lon: r.lon,
                            });
                          }
                        } catch (e) {
                          setIpGeo({ error: e instanceof Error ? e.message : 'lookup failed' });
                        }
                      }}
                      disabled={ipGeo?.loading}
                      className="text-[10px] text-blue-600 hover:text-blue-800 disabled:opacity-50"
                      title="Look up geo info via ip-api.com"
                    >
                      {ipGeo?.loading ? '…' : ipGeo ? 'refresh' : 'lookup'}
                    </button>
                  </div>
                  {ipGeo && !ipGeo.loading && (
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {ipGeo.error ? (
                        <span className="text-red-600">{ipGeo.error}</span>
                      ) : (
                        <>
                          <div>
                            {[ipGeo.city, ipGeo.regionName, ipGeo.country].filter(Boolean).join(', ')}
                            {ipGeo.countryCode ? ` (${ipGeo.countryCode})` : ''}
                          </div>
                          {ipGeo.isp && <div className="truncate" title={ipGeo.isp}>{ipGeo.isp}</div>}
                          {ipGeo.lat !== undefined && ipGeo.lon !== undefined && (
                            <a
                              href={`https://www.google.com/maps?q=${ipGeo.lat},${ipGeo.lon}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              map ↗
                            </a>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!summaryOnly && !isVerified && agentUid && currentChatId && (
        <div className="p-3 border-b border-gray-100 bg-amber-50">
          <div className="text-xs text-amber-900 mb-2">
            Customer identity has not been verified. Subscriber data is hidden until either the
            customer self-verifies (last-4 of CC) or an agent confirms identity manually.
          </div>
          <button
            type="button"
            onClick={() => setShowVerifyModal(true)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700"
          >
            <ShieldCheck className="w-3.5 h-3.5" /> Verify manually
          </button>
        </div>
      )}

      {!summaryOnly && agentUid && (
        <div className="px-3 pb-2 flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => setShowTicketModal(true)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
          >
            <Ticket className="w-3.5 h-3.5" /> Create ticket
          </button>
          {advanced && ((data.pastChats?.length ?? 0) > 0 || currentChatId) && (
            <button
              type="button"
              onClick={() => setShowMergeModal(true)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
              title="Merge two conversations from this contact"
            >
              <GitMerge className="w-3.5 h-3.5" /> Merge
            </button>
          )}
          {isBlocked && (
            <button
              type="button"
              onClick={doUnblock}
              disabled={actionBusy}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50"
            >
              <Lock className="w-3.5 h-3.5" /> Unblock
            </button>
          )}
        </div>
      )}

      {actionMsg && (
        <div className="mx-3 mb-2 px-2 py-1 text-[11px] rounded bg-gray-50 border border-gray-200 text-gray-700">
          {actionMsg}
        </div>
      )}

      {!summaryOnly && !collapsed && aiContext && (aiContext.isLead || aiContext.topic || aiContext.location || aiContext.device) && (
        <div className="mx-3 mb-2 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-violet-700 mb-1">
            ✨ Conversation context
          </div>
          <div className="flex flex-wrap gap-1.5 text-[11px]">
            {aiContext.isLead && (
              <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">🔥 Sales lead</span>
            )}
            {aiContext.topic && (
              <span className="px-1.5 py-0.5 rounded-full bg-white border border-violet-200 text-violet-800">{aiContext.topic}</span>
            )}
            {aiContext.location && (
              <span className="px-1.5 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700">
                📍 {aiContext.location === 'abroad' ? 'Abroad' : 'Israel'}
              </span>
            )}
            {aiContext.device && (
              <span className="px-1.5 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700">
                📱 {aiContext.device === 'iphone' ? 'iPhone' : 'Android'}
              </span>
            )}
          </div>
        </div>
      )}

      {!summaryOnly && !collapsed && (
        <div className="border-t border-gray-100">
          <nav className="flex border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
            {(
              [
                { id: 'overview', label: 'Customer' },
                { id: 'subs', label: 'Subscriptions' },
                { id: 'history', label: 'History' },
                { id: 'notes', label: 'Notes' },
              ] as const
            ).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`flex-1 px-2 py-1.5 text-[11px] font-medium border-b-2 transition-colors ${
                  activeTab === t.id
                    ? 'border-primary-600 text-primary-700 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>

          {activeTab === 'overview' && (
            <>
              {currentChatId && (
                <Section title="Verification" icon={<ShieldCheck className="w-3.5 h-3.5" />}>
                  <WpVerifyControls
                    tenantCode={tenantCode}
                    chatId={currentChatId}
                    onManualVerify={() => setShowVerifyModal(true)}
                  />
                </Section>
              )}
              {isVerified && currentChatId && (
                <Section title="WooCommerce" icon={<CreditCard className="w-3.5 h-3.5" />}>
                  <WpCustomerPanel tenantCode={tenantCode} chatId={currentChatId} />
                </Section>
              )}
            </>
          )}

          {activeTab === 'subs' && isVerified && currentChatId && (
            <>
              <Section title="Subscriptions" icon={<CreditCard className="w-3.5 h-3.5" />}>
                <WpSubscriptionsPanel
                  tenantCode={tenantCode}
                  chatId={currentChatId}
                  onSelect={(sid) => setSelectedSubscriptionId(sid)}
                />
              </Section>
              <Section title="Lines" icon={<Wifi className="w-3.5 h-3.5" />}>
                <WpLinesPanel tenantCode={tenantCode} chatId={currentChatId} />
              </Section>
            </>
          )}

          {activeTab === 'history' && (
            <Section title="Past conversations" icon={<History className="w-3.5 h-3.5" />}>
              {data.pastChats && data.pastChats.length > 0 ? (
                <div className="space-y-1">
                  {data.pastChats.map((p) => (
                    <Link
                      key={p.id}
                      to={`/inbox/${p.id}`}
                      className="block border border-gray-200 rounded p-1.5 text-xs hover:bg-gray-50"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1 text-gray-500 text-[10px]">
                          <MessageSquare className="w-3 h-3" />
                          {p.channel}
                        </span>
                        <span
                          className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                            STATUS_BADGE[p.status.toLowerCase()] ?? 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {p.status}
                        </span>
                      </div>
                      <div className="text-gray-800 mt-0.5 line-clamp-2" dir="auto">
                        {p.lastMessagePreview || '(no preview)'}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        {formatDate(p.lastMessageAt || p.createdAt)}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-[11px] text-gray-400 italic">No prior conversations.</div>
              )}
            </Section>
          )}

          {activeTab === 'notes' && (
            <>
              <Section title="Notes" icon={<StickyNote className="w-3.5 h-3.5" />}>
                {notes.length === 0 ? (
                  <div className="text-[11px] text-gray-400 italic mb-2">No notes yet.</div>
                ) : (
                  <ul className="space-y-1.5 mb-2">
                    {notes.map((n) => (
                      <li key={n.id} className="text-[11px] border border-gray-200 rounded p-1.5 bg-gray-50">
                        <div className="text-gray-800 whitespace-pre-wrap" dir="auto">
                          {n.note}
                        </div>
                        <div className="text-[10px] text-gray-400 mt-1 flex justify-between">
                          <span>{n.authorEmail || n.authorUid?.slice(0, 10)}</span>
                          <span>{formatDate(n.createdAt)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {agentUid && (
                  <div className="space-y-1">
                    <textarea
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      placeholder="Add a note…"
                      className="w-full text-[11px] border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      rows={2}
                    />
                    <button
                      type="button"
                      onClick={addNote}
                      disabled={noteBusy || !noteDraft.trim()}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                    >
                      Add note
                    </button>
                  </div>
                )}
              </Section>

              {agentUid &&
                (claims?.role === 'tenant_admin' || claims?.role === 'super_admin') && (
                  <Section title="Privacy" icon={<Shield className="w-3.5 h-3.5" />}>
                    <div className="text-[11px] text-gray-500 mb-2">
                      GDPR / right-to-be-forgotten controls. Admin only.
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <button
                        type="button"
                        onClick={doExport}
                        disabled={exporting}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-white border border-amber-300 text-amber-800 rounded hover:bg-amber-50 disabled:opacity-50"
                      >
                        <Download className="w-3 h-3" />
                        {exporting ? 'Exporting…' : 'Export all data'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDeleteModal(true)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-white border border-red-300 text-red-700 rounded hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" /> Delete all data
                      </button>
                    </div>
                  </Section>
                )}
            </>
          )}
        </div>
      )}

      {currentChatId && selectedSubscriptionId && (
        <WpDebtLedgerModal
          tenantCode={tenantCode}
          chatId={currentChatId}
          subscriptionId={selectedSubscriptionId}
          onClose={() => setSelectedSubscriptionId(null)}
        />
      )}

      {showBlockModal && agentUid && (
        <BlockContactModal
          onConfirm={doBlock}
          onCancel={() => setShowBlockModal(false)}
          busy={actionBusy}
        />
      )}

      {showDeleteModal && agentUid && (
        <DeleteContactModal
          onConfirm={doDelete}
          onCancel={() => setShowDeleteModal(false)}
          busy={actionBusy}
        />
      )}

      {showMergeModal && agentUid && (
        <MergeChatsModal
          currentChatId={currentChatId}
          pastChats={data.pastChats ?? []}
          onConfirm={doMerge}
          onCancel={() => setShowMergeModal(false)}
          busy={actionBusy}
        />
      )}

      {showTicketModal && agentUid && (
        <CreateTicketModal
          tenantCode={tenantCode}
          agentUid={agentUid}
          contactId={contactId}
          subscriberId={identity.subscriberId}
          onClose={(msg) => {
            setShowTicketModal(false);
            if (msg) setActionMsg(msg);
          }}
        />
      )}

      {/*
        No `agentUid` guard here: ManualVerifyModal takes no agentUid (it auths
        with the Firebase ID token), and WpVerifyControls' "Verify (last-4)"
        button is NOT gated on agentUid — a guard the opener doesn't share is
        just another way for the button to do nothing. `key` remounts the form
        on chat switch so the prefill can never belong to the previous customer.
      */}
      {showVerifyModal && currentChatId && (
        <ManualVerifyModal
          key={currentChatId}
          tenantCode={tenantCode}
          chatId={currentChatId}
          prefillEmail={identity?.email}
          prefillPhone={identity?.phone}
          onClose={async (msg) => {
            setShowVerifyModal(false);
            if (msg) {
              setActionMsg(msg);
              await refresh();
            }
          }}
        />
      )}

      {actionModal && agentUid && (
        <LineActionModal
          tenantCode={tenantCode}
          agentUid={agentUid}
          contactId={contactId}
          subscriberId={identity.subscriberId}
          lineId={actionModal.lineId}
          phoneNumber={actionModal.phoneNumber}
          action={actionModal.action}
          availablePlans={availablePlans}
          onClose={async (msg) => {
            setActionModal(null);
            if (msg) {
              setActionMsg(msg);
              await refresh();
            }
          }}
        />
      )}

      {/* Avoid TS6133 on lucide imports not used in all branches */}
      <span className="hidden">
        <MoreVertical className="w-0 h-0" />
      </span>
    </aside>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="p-3 border-b border-gray-100">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/40 z-[200] flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-md shadow-xl w-96 max-w-[95vw] p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function BlockContactModal({
  onConfirm,
  onCancel,
  busy,
}: {
  onConfirm: (reason: string) => void | Promise<void>;
  onCancel: () => void;
  busy: boolean;
}) {
  const [reason, setReason] = useState('');
  return (
    <ModalShell title="Block this contact" onClose={onCancel}>
      <p className="text-xs text-gray-600 mb-2">
        The contact will no longer be able to start new conversations. Existing chats remain
        visible but line actions are disabled.
      </p>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (optional)"
        rows={3}
        className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
      />
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="px-3 py-1 text-xs bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onConfirm(reason)}
          disabled={busy}
          className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
        >
          {busy ? 'Blocking…' : 'Block contact'}
        </button>
      </div>
    </ModalShell>
  );
}

function DeleteContactModal({
  onConfirm,
  onCancel,
  busy,
}: {
  onConfirm: (reason: string) => void | Promise<void>;
  onCancel: () => void;
  busy: boolean;
}) {
  const [reason, setReason] = useState('');
  const valid = reason.trim().length >= 3;
  return (
    <ModalShell title="Delete all contact data" onClose={onCancel}>
      <p className="text-xs text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1.5 mb-2">
        This requests a GDPR Article 17 deletion. Identifying data on chats, messages, and the
        contact record will be erased. This cannot be undone.
      </p>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        Reason (required)
      </label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
      />
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="px-3 py-1 text-xs bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => valid && onConfirm(reason.trim())}
          disabled={busy || !valid}
          className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
        >
          {busy ? 'Deleting…' : 'Delete all data'}
        </button>
      </div>
    </ModalShell>
  );
}

function MergeChatsModal({
  currentChatId,
  pastChats,
  onConfirm,
  onCancel,
  busy,
}: {
  currentChatId?: string;
  pastChats: NonNullable<Subscriber360['pastChats']>;
  onConfirm: (primary: string, secondary: string) => void | Promise<void>;
  onCancel: () => void;
  busy: boolean;
}) {
  const all = [
    ...(currentChatId ? [{ id: currentChatId, label: '(current conversation)' }] : []),
    ...pastChats.map((p) => ({
      id: p.id,
      label: `${p.channel} · ${p.status} · ${p.lastMessagePreview?.slice(0, 40) || '(no preview)'}`,
    })),
  ];
  const [primary, setPrimary] = useState<string>(all[0]?.id ?? '');
  const [secondary, setSecondary] = useState<string>(all[1]?.id ?? '');
  const canMerge = Boolean(primary && secondary && primary !== secondary);
  return (
    <ModalShell title="Merge conversations" onClose={onCancel}>
      <p className="text-xs text-gray-600 mb-2">
        Pick a primary (kept) and a secondary (folded in). All messages and attachments will be
        moved into the primary chat.
      </p>
      <label className="block text-xs font-medium text-gray-700 mt-2 mb-1">
        Primary (kept)
      </label>
      <select
        value={primary}
        onChange={(e) => setPrimary(e.target.value)}
        className="w-full text-xs border border-gray-300 rounded px-2 py-1"
      >
        {all.map((c) => (
          <option key={c.id} value={c.id}>
            {c.label}
          </option>
        ))}
      </select>
      <label className="block text-xs font-medium text-gray-700 mt-2 mb-1">
        Secondary (merged in)
      </label>
      <select
        value={secondary}
        onChange={(e) => setSecondary(e.target.value)}
        className="w-full text-xs border border-gray-300 rounded px-2 py-1"
      >
        {all.map((c) => (
          <option key={c.id} value={c.id}>
            {c.label}
          </option>
        ))}
      </select>
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="px-3 py-1 text-xs bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => canMerge && onConfirm(primary, secondary)}
          disabled={busy || !canMerge}
          className="px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
        >
          {busy ? 'Merging…' : 'Merge'}
        </button>
      </div>
    </ModalShell>
  );
}
